/**
 * New Trading Arrival, on the shared one-page arrival sheet (user, 2026-09-19). Writes nothing — the
 * document is created by the first lot, header and all. This static route wins over `$form/new`.
 *
 * The plant comes from the reconciled list, never the remembered id alone: posting a plant that has
 * since gone is what turns a stale browser into "facility not found".
 */
import { createFileRoute, redirect } from "@tanstack/react-router";
import { Callout } from "@/components/ui/callout";
import { FormPageSkeleton } from "@/components/app/skeletons";
import { ArrivalScreen } from "@/features/arrival/components/arrival-screen";
import { useAuth } from "@/features/auth/store";
import { useFacilities } from "@/features/auth/use-facilities";
import {
  gatePassSearch,
  useGatePassPrefill,
} from "@/features/gate-pass-in/arrival-prefill";
import { GatePassMissing } from "@/features/gate-pass-in/components/gate-pass-missing";

export const Route = createFileRoute("/_app/arrivals/trading/new")({
  /**
   * `?gatePass=` is how Gate › Inward Done starts an arrival against a Gate Pass In: the page opens
   * with that pass's transport filled in and its number shown, and the create links the two.
   */
  validateSearch: gatePassSearch,
  beforeLoad: () => {
    const auth = useAuth.getState();
    if (!auth.can("document.create") || !auth.canViewForm("arrival_trading"))
      throw redirect({ to: "/" });
  },
  component: Page,
});

function Page() {
  const { gatePass } = Route.useSearch();
  const { facilities, facilityId, ready } = useFacilities();
  const prefill = useGatePassPrefill(gatePass);
  if (!ready || prefill.loading) return <FormPageSkeleton />;
  if (prefill.missing) return <GatePassMissing />;
  if (facilities.length === 0)
    return (
      <Callout tone="danger" title="No plants to receive into">
        This login can see no processing plants, so there is nowhere for an
        arrival to land. Add one under Masters → Processing Plant, or ask an
        administrator to give your account a plant.
      </Callout>
    );
  if (facilityId === null)
    return (
      <Callout tone="warning" title="Choose a plant first">
        An arrival is received at one plant. Pick one from the plant selector at
        the top of the screen, then raise the arrival again.
      </Callout>
    );
  return (
    <ArrivalScreen
      // A different pass is a different blank arrival, not an edit of the one on screen.
      key={prefill.passId ?? "blank"}
      id={null}
      formCode="arrival_trading"
      facilityId={facilityId}
      initialHeader={prefill.initialHeader}
    />
  );
}
